import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { BellRing, Mail } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { authAPI } from "@/services/api";
import { AppDispatch } from "@/store";
import { updateCompany as updateCompanyState } from "@/store/slices/authSlice";
import { toast } from "sonner";

const formatDateTime = (value?: string | null) => {
  if (!value) return "todavia no registrado";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function InventoryAlertSettingsCard() {
  const dispatch = useDispatch<AppDispatch>();
  const { user, company, can } = useAuth();
  const canManageAlerts =
    can("inventory.manage") || can("inventory.configure") || can("settings.manage");

  const [isInitialized, setIsInitialized] = useState(false);
  const [form, setForm] = useState({
    enabled: true,
    recipients: "",
    notification_frequency_hours: "12",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!company?.id || isInitialized) return;

    const alertSettings = company?.settings?.inventory_alerts;
    if (alertSettings || company?.settings) {
      setForm({
        enabled: alertSettings?.enabled !== false,
        recipients: Array.isArray(alertSettings?.recipients)
          ? alertSettings?.recipients.join(", ")
          : "",
        notification_frequency_hours: String(
          alertSettings?.notification_frequency_hours || alertSettings?.cooldown_hours || 12,
        ),
      });
      setIsInitialized(true);
    }
  }, [company, isInitialized]);

  const recipients = useMemo(
    () =>
      form.recipients
        .split(/[\n,;]+/)
        .map((recipient) => recipient.trim().toLowerCase())
        .filter(Boolean)
        .filter((recipient, index, list) => list.indexOf(recipient) === index),
    [form.recipients],
  );

  const handleSave = async () => {
    if (!company?.id || !user?.id) return;

    setIsSaving(true);
    try {
      const nextSettings = {
        ...(company.settings || {}),
        inventory_alerts: {
          ...(company.settings?.inventory_alerts || {}),
          enabled: form.enabled,
          recipients,
          notification_frequency_hours: Math.max(
            1,
            Number(form.notification_frequency_hours || 12),
          ),
        },
      };

      const response = await authAPI.updateCompanySettings({
        company_id: company.id,
        user_id: user.id,
        settings: nextSettings,
      });

      dispatch(
        updateCompanyState({
          settings: response.data?.settings || nextSettings,
        }),
      );

      toast.success("Alertas automáticas actualizadas");
    } catch (error: unknown) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo guardar la configuración de alertas",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-slate-200/70 shadow-sm">
      <CardHeader className="border-b border-slate-100 bg-white">
        <CardTitle className="text-slate-900 flex items-center gap-2">
          <BellRing className="h-5 w-5 text-amber-500" />
          Alertas automáticas de inventario
        </CardTitle>
        <CardDescription>
          Centraliza destinatarios y frecuencia de reenvío del monitor de stock bajo.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-5">
        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div>
            <p className="font-medium text-slate-900">Activar monitor automático</p>
            <p className="text-sm text-slate-500">
              El proceso programado revisa el inventario y envía correo cuando un material cae
              bajo el mínimo.
            </p>
          </div>
          <Switch
            checked={form.enabled}
            disabled={!canManageAlerts || isSaving}
            onCheckedChange={(checked) => setForm((current) => ({ ...current, enabled: checked }))}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_0.6fr] gap-4">
          <div className="grid gap-2">
            <Label htmlFor="inventory-alert-recipients-settings">Correos destinatarios</Label>
            <Textarea
              id="inventory-alert-recipients-settings"
              disabled={!canManageAlerts || isSaving}
              placeholder="compras@empresa.com, supervisor@empresa.com"
              value={form.recipients}
              onChange={(event) =>
                setForm((current) => ({ ...current, recipients: event.target.value }))
              }
            />
            <p className="text-xs text-slate-500">
              Puedes separar correos por coma, punto y coma o salto de línea. Si lo dejas vacío,
              se usa owner/admin o el correo principal de la empresa como respaldo.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="inventory-alert-frequency-settings">
              Frecuencia mínima de reenvío
            </Label>
            <Input
              id="inventory-alert-frequency-settings"
              type="number"
              min="1"
              step="1"
              disabled={!canManageAlerts || isSaving}
              value={form.notification_frequency_hours}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  notification_frequency_hours: event.target.value,
                }))
              }
            />
            <p className="text-xs text-slate-500">
              Horas mínimas antes de reenviar la misma alerta si el escenario no cambió.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          <p className="font-medium text-slate-900 mb-1">Estado actual</p>
          <p>
            {form.enabled ? "Activo" : "Desactivado"} ·{" "}
            {recipients.length > 0
              ? `${recipients.length} destinatario(s) configurado(s)`
              : "sin destinatarios fijos"}
          </p>
          <p className="mt-1 flex items-center gap-2 text-slate-500">
            <Mail className="h-4 w-4" />
            Ultimo envio: {formatDateTime(company?.settings?.inventory_alerts?.last_sent_at)}
          </p>
        </div>

        {canManageAlerts && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Guardar alertas
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

